import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatoWa } from "./horariosModal";

import HttpManager from "../../../services/HttpManager";
import { ProspectEvent } from "../../../services/v2/prospects.service";
import { diaClave, diaEtiqueta, errorTexto, fechaDia, fechaLarga, hora, ORIGEN_LABEL } from "./salesHelpers";

// Íconos de línea con el color de cada tipo (antes emojis: Esteban 2026-10-06, "no se distinguen super bien").
const svg = (d: React.ReactNode) => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>;
const ICONO: { [k: string]: React.ReactNode } = {
	nota: svg(<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>),
	tarea: svg(<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></>),
	llamada: svg(<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />),
	whatsapp: svg(<path d="M21 12a8.5 8.5 0 0 1-12.6 7.4L3 21l1.6-5.2A8.5 8.5 0 1 1 21 12z" />),
	cambio: svg(<><path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></>),
	asignacion: svg(<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 11h-6M19 8v6" /></>),
	promo: svg(<><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z" /><circle cx="7" cy="7" r="1.5" /></>),
	sistema: svg(<><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" /></>),
};
const TITULO: { [k: string]: string } = { cambio: "", asignacion: "", sistema: "", promo: "Promo: " };

/** Lo que dice una llamada de VozMia o Zoho además del texto: duración, extensión, estado. */
const detalleLlamada = (ev: ProspectEvent): string | null => {
	const m = ev.meta || {};
	// VozMia firma como "Ext. 214": la extensión ya va en el autor.
	if (ev.origen === "vozmia") return m.wait && m.wait !== "0:00:00" ? "timbró " + m.wait : null;
	if (m.status || m.duration_s) return [m.type, m.status, m.duration_s ? m.duration_s + " s" : null].filter(Boolean).join(" · ");
	return null;
};

interface Props {
	campusId: number;
	prospectId: number;
	/** Tipos de la bitácora a mostrar; vacío = todo (Cronología). */
	kinds: string[];
	/** Sube cuando se guardó algo en la ficha. */
	refresco: number;
	/** Solo las primeras N, sin "Ver más" (Visión general · Últimas notas). */
	limite?: number;
	vacio: string;
	onTotal?: (n: number) => void;
	/** Pestaña Cronología: también quién la llevaba en Zoho, si era otra dueña. */
	cronologia?: boolean;
}

/** Bitácora de la ficha, lo más nuevo arriba y separada por día, como las listas relacionadas de Zoho. */
const CrmBitacora: React.FC<Props> = ({ campusId, prospectId, kinds, refresco, limite, vacio, onTotal, cronologia }) => {
	const http = HttpManager.getInstance();
	const per = limite || 50;
	const [events, setEvents] = useState<ProspectEvent[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [page, setPage] = useState<number>(1);
	const [cargando, setCargando] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [editando, setEditando] = useState<number | null>(null);
	const [texto, setTexto] = useState<string>("");
	const [titulo, setTitulo] = useState<string>("");
	const vivo = useRef<string>("");
	const clave = prospectId + "|" + kinds.join(",");

	const cargar = useCallback(async (p: number) => {
		vivo.current = clave;
		setCargando(true); setError(null);
		try {
			const r = await http.prospectsService.events(campusId, prospectId, { kind: kinds, cronologia }, per, p);
			if (vivo.current !== clave) return;
			setEvents((prev) => (p === 1 ? r.data.data : [...prev, ...r.data.data.filter((e) => !prev.some((x) => x.id === e.id))]));
			setTotal(r.data.total); setPage(p);
			if (onTotal) onTotal(r.data.total);
		} catch (e) { setError(errorTexto(e)); }
		finally { setCargando(false); }
	}, [campusId, clave, cronologia]);

	useEffect(() => { setEvents([]); setEditando(null); cargar(1); }, [cargar, refresco]);

	const guardarEdicion = async (ev: ProspectEvent) => {
		if (!texto.trim()) return;
		try {
			const t = titulo.trim().replace(/\s+/g, " ");
			await http.prospectsService.editNote(campusId, prospectId, ev.id, texto.trim(), t);
			setEvents((prev) => prev.map((x) => (x.id === ev.id ? { ...x, body: texto.trim(), meta: { ...(x.meta || {}), titulo: t || undefined } } : x)));
			setEditando(null);
		} catch (e) { setError(errorTexto(e)); }
	};

	let ultimoDia = -1;
	return (
		<div>
			{error ? <div className="crm-aviso error">{error}</div> : null}
			{events.map((ev) => {
				const dia = diaClave(ev.occurred_at);
				const sep = !limite && dia !== ultimoDia ? <div className="crm-dia">{diaEtiqueta(ev.occurred_at)}</div> : null;
				ultimoDia = dia;
				const det = ev.kind === "llamada" ? detalleLlamada(ev) : null;
				const tarea = ev.kind === "tarea" && ev.meta?.status ? ev.meta.status + (ev.meta.due ? " · vence " + fechaDia(ev.meta.due) : "") : null;
				const sale = ev.kind === "whatsapp" ? (ev.direction === "out" ? "Enviado · " : "Recibido · ") : "";
				return (
					<React.Fragment key={ev.id}>
						{sep}
						<div className="crm-it">
							<div className={"crm-ico " + ev.kind} aria-hidden="true">{ICONO[ev.kind] || "•"}</div>
							<div className="tx">
								{editando === ev.id ? (
									<div className="crm-escribir" style={{ margin: 0 }}>
										<input className="crm-nota-titulo" value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={120} placeholder="Título (opcional)" aria-label="Título de la nota" />
										<textarea value={texto} onChange={(e) => setTexto(e.target.value)} aria-label="Editar nota" autoFocus />
										<div className="fila">
											<button className="crm-b chico" onClick={() => setEditando(null)}>Cancelar</button>
											<button className="crm-b chico azul" disabled={!texto.trim()} onClick={() => guardarEdicion(ev)}>Guardar</button>
										</div>
									</div>
								) : (
									<>{ev.kind === "nota" && ev.meta?.titulo && !(ev.body || "").startsWith(ev.meta.titulo) ? <b className="crm-nota-tit">{ev.meta.titulo}</b> : null}{TITULO[ev.kind] ?? ""}{sale}{ev.body ? (ev.kind === "whatsapp" ? formatoWa(ev.body) : ev.body) : <span className="crm-apagado">(sin texto)</span>}</>
								)}
								<div className="meta">
									{[ev.author_name, limite ? fechaLarga(ev.occurred_at) : hora(ev.occurred_at), det, tarea].filter(Boolean).join(" · ")}
									{ev.meta?.editada_at ? " · editada" : ""}
									{ev.origen !== "wapp" ? <span className="crm-orig">{ORIGEN_LABEL[ev.origen] || ev.origen}</span> : null}
									{ev.hasRecording ? <span className="crm-orig">Grabada</span> : null}
								</div>
							</div>
							{(ev as any).mine && editando !== ev.id ? (
								<div className="der"><button className="crm-b chico" onClick={() => { setEditando(ev.id); setTexto(ev.body || ""); setTitulo(ev.meta?.titulo || ""); }}>Editar</button></div>
							) : null}
						</div>
					</React.Fragment>
				);
			})}
			{!events.length && !cargando ? <div className="crm-pie">{vacio}</div> : null}
			{cargando && !events.length ? <div className="crm-pie">Cargando…</div> : null}
			{!limite && events.length < total ? (
				<button className="crm-b" disabled={cargando} onClick={() => cargar(page + 1)}>{cargando ? "Cargando…" : "Ver más (" + (total - events.length).toLocaleString("es-MX") + ")"}</button>
			) : null}
		</div>
	);
};

export default CrmBitacora;
